import React, { useEffect, useMemo, useCallback, useState } from "react";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import {
  Box,
  Flex,
  HStack,
  Stack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { ChevronDownIcon, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useStores } from "stores/setup/use-store";
import { ConfirmationModal } from "components/SensorAlertSection/ConfirmationModal";
import { ToggleBlock } from "./ToggleBlock";
import { InsightPreferencesBlock } from "./InsightPreferencesBlock";
import { AlertPreferencesBlock } from "./AlertPreferencesBlock";
import { DebouncedInput, DebouncedTextarea } from "./DebouncedFormFields";
import {
  EventPreferencesStepProps,
  InsightColorId,
  INSIGHT_COLOR_LIST,
} from "../types";

/**
 * EventPreferencesStep - Step 3 of Event creation
 * Contains two toggle blocks:
 * - Block 1: Insight preferences (always active, dashboard required)
 * - Block 2: Alert preferences (optional toggle)
 *
 * FR-010: Two independent toggle blocks
 * FR-012: At least one block must be active (Block 1 is always active for Events)
 */
export const EventPreferencesStep: React.FC<EventPreferencesStepProps> =
  observer(
    ({
      values,
      errors,
      touched,
      handleChange,
      setFieldValue,
      t,
      usersTableSelected,
      setUsersTableSelected,
      alertUsers,
      isDashboardLocked = false,
    }) => {
      // Block 1 (Insight) is always active for Events - ensure it's true when component mounts
      useEffect(() => {
        if (!values.block1Active) {
          setFieldValue("block1Active", true);
        }
      }, [values.block1Active, setFieldValue]);

      // FR-024: Auto-enable block2Active when sensor alert is configured.
      // Only fires on sensorAlertConfig change so the user can freely toggle
      // block2Active off without this effect re-enabling it.
      useEffect(() => {
        const config = values.sensorAlertConfig;
        if (!config) return;
        const hasSensors = config.sensors.length > 0;
        const hasConditions =
          config.conditions.length > 0 &&
          config.conditions.some(
            (g: ConditionGroup) => g.conditions && g.conditions.length > 0,
          );
        if (hasSensors && hasConditions) {
          setFieldValue("block2Active", true);
        }
      }, [values.sensorAlertConfig, setFieldValue]);

      const {
        dataStores: { insightAlertStore },
      } = useStores();

      const { t: tSensor } = useTranslation("translation", {
        keyPrefix: "oceanWeatherIntelligence.sensorAlert",
      });

      const [isStep3DisableModalOpen, setIsStep3DisableModalOpen] =
        useState(false);

      // FR-025, FR-026: Intercept block2Active toggle OFF when sensor config exists
      const handleBlock2Toggle = (active: boolean) => {
        if (!active) {
          const config = values.sensorAlertConfig;
          const hasSensors = config?.sensors && config.sensors.length > 0;
          if (hasSensors) {
            // Show confirmation modal before disabling
            setIsStep3DisableModalOpen(true);
            return;
          }
        }
        setFieldValue("block2Active", active);
      };

      const handleStep3DisableConfirm = useCallback(() => {
        setIsStep3DisableModalOpen(false);
        // Disable both alert and sensor config
        setFieldValue("block2Active", false);
        setFieldValue("sensorAlertConfig", {
          sensors: [],
          conditions: [],
        });
        // Clear sensor selection in store
        insightAlertStore.clearSensorSelection();
      }, [setFieldValue, insightAlertStore]);

      const selectedColor = useMemo(() => {
        return (
          INSIGHT_COLOR_LIST.find((c) => c.id === values.insightColor) ||
          INSIGHT_COLOR_LIST[0]
        );
      }, [values.insightColor]);

      const getColorName = useCallback(
        (colorId: InsightColorId) => {
          const colorNames: Record<InsightColorId, string> = {
            1: t("red"),
            2: t("yellow"),
            3: t("blue"),
            4: t("orange"),
          };
          return colorNames[colorId];
        },
        [t],
      );

      return (
        <Box>
          <Stack spacing={8}>
            {/* Event Name + Color Selector Row */}
            <Flex gap="32px" align="flex-end">
              {/* Event Name */}
              <FormControl
                isRequired
                isInvalid={!!errors.name && touched.name}
                flex={1}
              >
                <FormLabel color="#414651" fontWeight={"500"}>
                  {t("alertName")}
                </FormLabel>
                <DebouncedInput
                  name="name"
                  placeholder={t("exampleRisk") as string}
                  formikValue={values.name}
                  onSyncValue={(val) => setFieldValue("name", val)}
                  maxLength={70}
                  borderColor="#D5D7DA"
                />
                <FormErrorMessage mt={4}>
                  {t("thisFieldRequired")}
                </FormErrorMessage>
              </FormControl>

              {/* Color Selector */}
              <Box flexShrink={0}>
                <Text
                  fontSize="14px"
                  fontWeight="500"
                  color="#414651"
                  lineHeight="20px"
                  mb="8px"
                >
                  {t("colorOnDashboard")}
                </Text>
                <Menu autoSelect={false}>
                  <MenuButton
                    as={Box}
                    cursor="pointer"
                    bg="white"
                    border="1px solid #D5D7DA"
                    borderRadius="8px"
                    px="14px"
                    py="6px"
                    h="40px"
                    w="146px"
                    _hover={{ borderColor: "gray.400" }}
                  >
                    <Flex align="center" justify="space-between">
                      <HStack spacing="8px">
                        <Box
                          w="8px"
                          h="8px"
                          bg={selectedColor.color}
                          borderRadius="full"
                        />
                        <Text
                          fontSize="16px"
                          fontWeight="400"
                          color="#717680"
                          lineHeight="24px"
                        >
                          {getColorName(selectedColor.id)}
                        </Text>
                      </HStack>
                      <ChevronDownIcon size={20} color="#717680" />
                    </Flex>
                  </MenuButton>
                  <MenuList minW="146px" zIndex={10}>
                    {INSIGHT_COLOR_LIST.map((color) => (
                      <MenuItem
                        key={color.id}
                        onClick={() => setFieldValue("insightColor", color.id)}
                      >
                        <HStack spacing="8px">
                          <Box
                            w="8px"
                            h="8px"
                            bg={color.color}
                            borderRadius="full"
                          />
                          <Text fontSize="16px" color="#717680">
                            {getColorName(color.id)}
                          </Text>
                        </HStack>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Box>
            </Flex>

            {/* Event Description */}
            <FormControl>
              <FormLabel color="#414651" fontWeight="500">
                {t("eventDescriptionLabel")}
              </FormLabel>
              <DebouncedTextarea
                name="resume"
                placeholder={t("eventDescriptionPlaceholder") as string}
                formikValue={values.resume || ""}
                onSyncValue={(val) => setFieldValue("resume", val)}
                maxLength={400}
                rows={3}
                resize="vertical"
                borderColor="#D5D7DA"
              />
            </FormControl>

            {/* Block 1: Insight preferences - Dashboard settings (always active, dashboard required) */}
            <FormControl>
              <InsightPreferencesBlock
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                t={t}
                isDashboardLocked={isDashboardLocked}
              />
            </FormControl>

            {/* Block 2: Alert preferences - "Quero ser alertado" */}
            <FormControl>
              <ToggleBlock
                title={t("wantAlerts")}
                isActive={values.block2Active}
                onToggle={handleBlock2Toggle}
                icon={<Send size={20} color="#086AA9" />}
              >
                <AlertPreferencesBlock
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  t={t}
                  usersTableSelected={usersTableSelected}
                  setUsersTableSelected={setUsersTableSelected}
                  alertUsers={alertUsers}
                />
              </ToggleBlock>
            </FormControl>
          </Stack>

          {/* Step 3 disable confirmation modal (FR-025, FR-026) */}
          <ConfirmationModal
            isOpen={isStep3DisableModalOpen}
            onClose={() => setIsStep3DisableModalOpen(false)}
            onConfirm={handleStep3DisableConfirm}
            title={tSensor("step3DisableModalTitle")}
            description={tSensor("step3DisableModalDescription")}
            cancelLabel={tSensor("step3DisableCancel")}
            confirmLabel={tSensor("step3DisableConfirm")}
            closeAriaLabel={tSensor("closeAriaLabel") ?? undefined}
          />
        </Box>
      );
    },
  );

EventPreferencesStep.displayName = "EventPreferencesStep";

export default EventPreferencesStep;
